import React, { useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';

// components
import SecondaryNav from './SecondaryNav';
import {
    Box,
    Chip,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    Stack,
    Toolbar,
    Typography,
} from '@mui/material';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import { Link as MUILink } from '@mui/material';

// icons
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import WarningIcon from '@mui/icons-material/Warning';
import { BANNER_HEIGHT_CSS } from './BannerManager';

const DRAWER_WIDTH = '100%';
const TOOLBAR_HEIGHT = '44px';

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.standard,
  }),
  ...(open && {
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.standard,
    }),
  }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 2),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  minHeight: '44px',
  justifyContent: 'space-between',
}));

const MobileNotSupported = styled('div')(({theme}) => ({
  backgroundColor: '#1C1C1C',
  color: 'rgba(255, 255, 255, 0.70)',
  padding: '10px 15px',
}));

const StyledMUILink = styled(MUILink)(({theme}) => ({
  [theme.breakpoints.down('sm')]: {
    color: theme.palette.common.white,
    width: '100%'
  }
}));

const ToolsListStyled = styled(List)(({theme}) => ({
  paddingTop: 0,
  paddingBottom: 0,
}));

const ToolNavListItemStyled = styled(ListItem)(({theme}) => ({
  [theme.breakpoints.down('sm')]: {
    borderTop: '1px solid #1C1C1C',
    width: '100%',
  }
}));

const ToolDescriptionText = styled(Typography)(({theme}) => ({
  color: 'rgba(255, 255, 255, 0.87)',
  fontFamily: 'Inter',
  fontSize: '12px',
  fontWeight: 'normal',
  fontStyle: 'normal',
  lineHeight: '140%'
}));

export default function ToolbarDrawer({
  isMobileView,
  league,
  secondaryLinks,
  selectedView,
  toolItems,
}) {
  const theme = useTheme();

  const [open, setOpen] = useState(false);
  let mobileNavItems = toolItems.reduce((acc, curr) => {
    if (curr.isMobileSupported) {
        acc.supported.push(curr);
    } else {
        acc.notSupported.push(curr);
    }
    return acc;
  },{
    notSupported: [],
    supported: []
  });
  
  const selectedTool = toolItems.find(item => item.view === selectedView) || {};

  const handleDrawerToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };


  const ToolNavItem = ({link, name, description, view, isMobileSupported, isBeta}) => {
    return (
      <ToolNavListItemStyled disablePadding>
        <StyledMUILink
          underline='none'
          href={link}
          >
          <ListItemButton selected={selectedView === view} disableGutters sx={{ [theme.breakpoints.down('sm')]: {p: '15px' }}}>
            <Stack direction='column' rowGap='6px'>
             <Stack direction='row' alignItems='center' spacing={1}>
                <Typography fontFamily='Inter' variant='subtitle1' component='div' fontWeight={700} fontSize='16px' lineHeight='normal'>{name}</Typography>
                {isBeta && (
                  <Chip
                    label="Beta"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '10px',
                      fontWeight: 600,
                      backgroundColor: '#42A5F5',
                      color: '#000',
                      '& .MuiChip-label': {
                        px: 0.75,
                      },
                    }}
                  />
                )}
                {
                  isMobileView && !isMobileSupported && (
                    <ListItemIcon>
                      <WarningIcon sx={{color: 'rgba(255, 250, 157, 0.7)', width:'19px', height: '19px'}} />
                    </ListItemIcon>
                  )
                }
              </Stack>
              {
                isMobileView && description && (
                  <ToolDescriptionText fontFamily='Inter' variant='caption'>{description}</ToolDescriptionText>
                )
              }
            </Stack>
          </ListItemButton>
        </StyledMUILink>
      </ToolNavListItemStyled>
    )
  }

  const renderNavItems = (navItems) => {
    if (navItems && navItems.length === 0) {
      return null;
    }

    return (
      <>
        {
          navItems.map((item, index) => (
            <ToolNavItem {...item} key={item.name + '-' + index} />
          ))
        }
      </>
    )
  }

  return (
    <Box sx={{ mb: TOOLBAR_HEIGHT }}>
      {/* Sit below the app-level promo banner (if one is showing) instead of covering it. */}
      <AppBar position="fixed" open={open} sx={{ backgroundColor: theme.palette.common.black, top: BANNER_HEIGHT_CSS }}>
        <Toolbar sx={{ columnGap: '15px', justifyContent: 'space-between', minHeight: TOOLBAR_HEIGHT }}>
          <Stack direction='row' alignSelf='stretch' alignItems={'center'}>
            <IconButton
              color='inherit'
              aria-label='open drawer'
              onClick={handleDrawerToggle}
              edge='start'
              sx={{ p: '10px', ...(open && { display: 'none' }) }}
            >
              <MenuIcon />
            </IconButton>
            <Typography fontFamily='Inter' variant="h6" noWrap component="div" fontSize='16px' fontWeight={800}>
              {selectedTool.name}
            </Typography>
          </Stack>
          <Stack height={20} width={65}>
            <img
              src={`${process.env.PUBLIC_URL}/images/logo.png`}
              alt=""
            />
          </Stack>
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            backgroundColor: theme.palette.common.black,
            color: theme.palette.common.white,
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <DrawerHeader>
          <Typography fontFamily='Inter' variant='subtitle1' component='div' fontWeight={700} fontSize='16px' lineHeight='normal'>Stokastic Tools</Typography>
          <IconButton color='inherit' onClick={handleDrawerToggle} aria-label='close drawer' sx={{ p: 0 }}>
            <CloseIcon />
          </IconButton>
        </DrawerHeader>
        <ToolsListStyled>
          { renderNavItems(mobileNavItems?.supported) }
        </ToolsListStyled>
        {
          mobileNavItems?.notSupported?.length > 0 && (
            <>
              <MobileNotSupported key='mobile-not-supported'>
                <Stack direction='row' alignItems='center' columnGap='12px'>
                  <WarningIcon sx={{ color: 'rgba(255, 250, 157, 0.7)', width:'19px', height: '19px' }} />
                  <Typography fontFamily='Inter' variant='caption' fontWeight={700}>Mobile support not optimized for these tools</Typography>
                </Stack>
              </MobileNotSupported>
              <ToolsListStyled>
                { renderNavItems(mobileNavItems?.notSupported) }
              </ToolsListStyled>
            </>
          )
        }
        <SecondaryNav navItems={secondaryLinks} league={league} />
      </Drawer>
    </Box>
  );
}